import {DocumentPriceBasis} from "@/components/docs/DocumentPriceBasis";
import { useTranslation } from "react-i18next";
import { Mail, Phone, User } from "lucide-react";
import type { QuotationDetailDto } from "@hisense/shared";
import { Panel } from "@/components/ui/Panel";
import { TableShell, Th, Td, Tr } from "@/components/ui/Table";
import { formatDate } from "@/domain/datetime";
import { formatUnitPrice } from "@/domain/money";

export function QuotationDocument({
  quotation: q,
}: {
  quotation: QuotationDetailDto;
}) {
  const { t } = useTranslation();
  const customer = q.printCustomer;
  const price = (value: number | null | undefined, currency: string) =>
    value == null ? "—" : formatUnitPrice(value, currency, false);
  const metadata = [
    ["pi.date", formatDate(q.issueDate)],
    ["quotations.printingDate", formatDate(q.printingDate)],
    ["quotations.preparedBy", q.preparedBy || "—"],
    ["common.currencyLabel", q.currency],
    ["quotationWorkspace.validUntil", formatDate(q.validUntil)],
  ];
  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-2">
        <Panel className="p-4">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("pi.billTo")}
          </h2>
          {customer ? (
            <div className="space-y-2 text-sm">
              <p className="font-medium">{customer.companyName}</p>
              {customer.address && (
                <p className="whitespace-pre-line text-ink-soft">
                  {customer.address}
                </p>
              )}
              {q.attn && (
                <p className="flex items-center gap-2">
                  <User
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-ink-faint"
                  />
                  {q.attn}
                </p>
              )}
              {customer.phone && (
                <p className="flex items-center gap-2">
                  <Phone
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-ink-faint"
                  />
                  <span className="font-mono">{customer.phone}</span>
                </p>
              )}
              {customer.email && (
                <p className="flex items-center gap-2">
                  <Mail
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-ink-faint"
                  />
                  <span className="break-all">{customer.email}</span>
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-ink-soft">
              {t("quotations.unknownCustomer")}
            </p>
          )}
        </Panel>
        <Panel className="p-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            {metadata.map(([key, value]) => (
              <div key={key}>
                <dt className="mb-1 text-xs text-ink-faint">{t(key!)}</dt>
                <dd className="font-mono tabular">{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>
      <p className="text-xs text-ink-soft">
        {t("quotationWorkspace.priceScheduleHint")}
      </p>
      <DocumentPriceBasis snapshots={q.priceSnapshots}/>
      <TableShell>
        <thead>
          <tr>
            <Th>#</Th>
            <Th pin>{t("common.code")}</Th>
            <Th>{t("common.subRef")}</Th>
            <Th>{t("common.description")}</Th>
            <Th>{t("common.size")}</Th>
            <Th>{t("common.color")}</Th>
            <Th>{t("common.unit")}</Th>
            <Th>{t("common.currencyLabel")}</Th>
            {[
              "unitPrice",
              "exwPrice",
              "dtdPrice",
              "underMoqPrice",
              "moq",
              "mcq",
              "msq",
            ].map((key) => (
              <Th key={key} align="right">
                {t(`common.${key}`)}
              </Th>
            ))}
            <Th>{t("common.sampleLeadTime")}</Th>
            <Th>{t("common.bulkLeadTime")}</Th>
            <Th>{t("common.lineRemark")}</Th>
          </tr>
        </thead>
        <tbody>
          {q.items.map((item, index) => (
            <Tr key={item.id || index} className="group/row">
              <Td className="font-mono text-ink-faint">{index + 1}</Td>
              <Td pin>
                <div className="flex items-center gap-2 py-2">
                  {item.imageUrl && (
                    <img
                      loading="lazy"
                      src={item.imageUrl}
                      alt=""
                      className="size-12 shrink-0 rounded-sm border border-line object-contain"
                    />
                  )}
                  <span className="font-mono">{item.productCode || "—"}</span>
                </div>
              </Td>
              <Td className="font-mono">{item.subRef || "—"}</Td>
              <Td className="max-w-72 whitespace-normal py-3">
                {item.description || "—"}
              </Td>
              <Td>{item.size || "—"}</Td>
              <Td>{item.color || "—"}</Td>
              <Td>{item.unit || "—"}</Td>
              <Td className="font-mono">{item.currency || q.currency}</Td>
              {[
                item.unitPrice,
                item.exwPrice,
                item.dtdPrice,
                item.underMoqPrice,
              ].map((value, i) => (
                <Td key={i} align="right" className="font-mono tabular">
                  {price(value, item.currency || q.currency)}
                </Td>
              ))}
              {[item.moq, item.mcq, item.msq].map((value, i) => (
                <Td key={i} align="right" className="font-mono tabular">
                  {value ?? "—"}
                </Td>
              ))}
              <Td>{item.sampleLeadTime || "—"}</Td>
              <Td>{item.bulkLeadTime || "—"}</Td>
              <Td className="max-w-72 whitespace-pre-line py-3 text-xs text-ink-soft">
                {item.remark || "—"}
              </Td>
            </Tr>
          ))}
        </tbody>
      </TableShell>
      <div className="grid gap-3 lg:grid-cols-2">
        {[
          ["common.remarks", q.remarks],
          ["quotationWorkspace.validityTerms", q.validityTerms],
        ].map(([key, value]) => (
          <Panel key={key} className="p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
              {t(key!)}
            </h2>
            <p className="whitespace-pre-wrap text-sm">{value || "—"}</p>
          </Panel>
        ))}
      </div>
    </div>
  );
}
